import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy } from 'lucide-react';
import { showToast } from '@/utils/toast';

async function writeClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback */
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

/**
 * 悬浮复制：图标默认跟在文字末尾（换行后也跟最后一字右侧）。
 * @param {boolean} [popoverCopy] 标题等长文案：行内不显示复制；悬浮气泡展示全文，复制图标放在气泡文字末尾
 */
export default function HoverCopyField({
  label,
  value,
  display,
  emptyText = '—',
  className = '',
  toastOnCopy = true,
  separator = '：',
  popoverCopy = false,
}) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const leaveTimerRef = useRef(null);

  const raw = value == null ? '' : String(value).trim();
  const canCopy = Boolean(raw) && raw !== '—' && raw !== '-';
  const shown = display != null ? display : (canCopy ? raw : emptyText);

  const clearLeaveTimer = useCallback(() => {
    if (leaveTimerRef.current) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  const updatePos = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxWidth = Math.min(520, Math.max(280, window.innerWidth - rect.left - 16));
    let left = rect.left;
    if (left + maxWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - maxWidth - 8);
    }
    setPos({
      top: rect.bottom + 6,
      left,
      maxWidth,
    });
  }, []);

  const openPopover = useCallback(() => {
    if (!popoverCopy || !canCopy) return;
    clearLeaveTimer();
    updatePos();
    setOpen(true);
  }, [popoverCopy, canCopy, clearLeaveTimer, updatePos]);

  const scheduleClose = useCallback(() => {
    clearLeaveTimer();
    leaveTimerRef.current = window.setTimeout(() => {
      setOpen(false);
    }, 160);
  }, [clearLeaveTimer]);

  const keepOpen = useCallback(() => {
    clearLeaveTimer();
    setOpen(true);
  }, [clearLeaveTimer]);

  useEffect(() => () => clearLeaveTimer(), [clearLeaveTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const onScrollOrResize = () => updatePos();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePos]);

  const onCopy = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canCopy) return;
    const ok = await writeClipboard(raw);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
      if (toastOnCopy) {
        showToast(label ? fixedUiTpl('已复制{0}', label) : fixedUi('已复制'), 'success');
      }
    } else if (toastOnCopy) {
      showToast(fixedUi('复制失败'), 'error');
    }
  }, [canCopy, raw, label, toastOnCopy]);

  const copyBtn = canCopy ? (
    <button
      type="button"
      className={`tiktok-hover-copy__btn${popoverCopy ? ' tiktok-hover-copy__btn--always' : ''}`}
      onClick={onCopy}
      title={copied ? fixedUi('已复制') : fixedUiTpl('复制{0}', label || '')}
      aria-label={copied ? fixedUi('已复制') : fixedUiTpl('复制{0}', label || fixedUi('内容'))}
    >
      {copied ? <Check size={13} strokeWidth={2.4} /> : <Copy size={13} strokeWidth={2.2} />}
    </button>
  ) : null;

  if (popoverCopy) {
    return (
      <>
        <span
          ref={triggerRef}
          className={`tiktok-hover-copy tiktok-hover-copy--popover-trigger${className ? ` ${className}` : ''}${canCopy ? '' : ' tiktok-hover-copy--empty'}`}
          onMouseEnter={openPopover}
          onMouseLeave={scheduleClose}
          onFocus={openPopover}
          onBlur={scheduleClose}
        >
          <span className="tiktok-hover-copy__text">{shown || emptyText}</span>
        </span>
        {open && pos && canCopy && typeof document !== 'undefined'
          ? createPortal(
            <div
              className="tiktok-hover-copy-popover"
              style={{ top: pos.top, left: pos.left, maxWidth: pos.maxWidth }}
              onMouseEnter={keepOpen}
              onMouseLeave={scheduleClose}
              role="tooltip"
            >
              <span className="tiktok-hover-copy-popover__text">{raw}</span>
              {copyBtn}
            </div>,
            document.body,
          )
          : null}
      </>
    );
  }

  return (
    <span className={`tiktok-hover-copy${className ? ` ${className}` : ''}${canCopy ? '' : ' tiktok-hover-copy--empty'}`}>
      <span className="tiktok-hover-copy__body">
        {label ? (
          <span className="tiktok-hover-copy__label">
            {label}
            {separator}
          </span>
        ) : null}
        <span className="tiktok-hover-copy__text" title={canCopy ? raw : undefined}>
          {shown}
        </span>
      </span>
      {copyBtn}
    </span>
  );
}
